import type { AutoApproveBehavior, AutoApproveMode } from './schema';

export const DEFAULT_AUTO_APPROVE_BEHAVIOR: AutoApproveBehavior = 'disabled';
export const DEFAULT_AUTO_APPROVE_MODE: AutoApproveMode = 'disabled';
export const DEFAULT_ENABLED_AUTO_APPROVE_MODE: AutoApproveMode = 'low_risk_only';

type AutoApproveSettingsLike = {
  autoApproveBehavior?: AutoApproveBehavior | null;
  autoApproveMode?: AutoApproveMode | null;
};

export type NormalizedAutoApproveUpdate = {
  autoApproveBehavior: AutoApproveBehavior;
  autoApproveMode: AutoApproveMode;
  shouldWriteBehavior: boolean;
  shouldWriteMode: boolean;
};

export function resolveAutoApproveSettings(settings?: AutoApproveSettingsLike | null): {
  autoApproveBehavior: AutoApproveBehavior;
  autoApproveMode: AutoApproveMode;
} {
  const explicitMode = settings?.autoApproveMode ?? undefined;
  const explicitBehavior = settings?.autoApproveBehavior ?? undefined;

  if (
    explicitMode === DEFAULT_AUTO_APPROVE_MODE ||
    explicitBehavior === DEFAULT_AUTO_APPROVE_BEHAVIOR
  ) {
    return {
      autoApproveBehavior: DEFAULT_AUTO_APPROVE_BEHAVIOR,
      autoApproveMode: DEFAULT_AUTO_APPROVE_MODE,
    };
  }

  const autoApproveBehavior =
    explicitBehavior ?? (explicitMode === undefined ? DEFAULT_AUTO_APPROVE_BEHAVIOR : 'live');

  const autoApproveMode =
    explicitMode ??
    (autoApproveBehavior === DEFAULT_AUTO_APPROVE_BEHAVIOR
      ? DEFAULT_AUTO_APPROVE_MODE
      : DEFAULT_ENABLED_AUTO_APPROVE_MODE);

  return {
    autoApproveBehavior,
    autoApproveMode,
  };
}

function getBehaviorForPartialUpdate(params: {
  explicitBehavior?: AutoApproveBehavior;
  explicitMode?: AutoApproveMode;
  existingBehavior?: AutoApproveBehavior;
}): AutoApproveBehavior | undefined {
  const { explicitBehavior, explicitMode, existingBehavior } = params;

  if (explicitBehavior !== undefined) {
    return explicitBehavior;
  }

  if (explicitMode === undefined) {
    return existingBehavior;
  }

  const explicitlyDisablesAutoApprove = explicitMode === DEFAULT_AUTO_APPROVE_MODE;
  if (explicitlyDisablesAutoApprove) {
    return undefined;
  }

  const hasExistingEnabledBehavior =
    existingBehavior && existingBehavior !== DEFAULT_AUTO_APPROVE_BEHAVIOR;
  if (hasExistingEnabledBehavior) {
    return existingBehavior;
  }

  return undefined;
}

export function normalizeAutoApproveUpdate(
  input: AutoApproveSettingsLike,
  existingSettings?: AutoApproveSettingsLike | null,
): NormalizedAutoApproveUpdate {
  const explicitBehavior = input.autoApproveBehavior ?? undefined;
  const explicitMode = input.autoApproveMode ?? undefined;
  const modeWasExplicitlySet = input.autoApproveMode !== undefined;
  const behaviorWasExplicitlySet = input.autoApproveBehavior !== undefined;
  const existingBehavior = existingSettings?.autoApproveBehavior ?? undefined;
  const existingMode = existingSettings?.autoApproveMode ?? undefined;
  const resolved = resolveAutoApproveSettings({
    autoApproveBehavior: getBehaviorForPartialUpdate({
      explicitBehavior,
      explicitMode,
      existingBehavior,
    }),
    autoApproveMode:
      explicitMode ??
      (explicitBehavior !== undefined &&
      explicitBehavior !== DEFAULT_AUTO_APPROVE_BEHAVIOR &&
      existingMode === DEFAULT_AUTO_APPROVE_MODE
        ? undefined
        : existingMode),
  });

  const inferredBehaviorFromMode =
    !behaviorWasExplicitlySet &&
    modeWasExplicitlySet &&
    resolved.autoApproveBehavior !== existingBehavior;
  const inferredModeFromBehavior =
    !modeWasExplicitlySet &&
    behaviorWasExplicitlySet &&
    (resolved.autoApproveMode !== (existingMode ?? DEFAULT_AUTO_APPROVE_MODE) ||
      resolved.autoApproveBehavior === DEFAULT_AUTO_APPROVE_BEHAVIOR);

  return {
    ...resolved,
    shouldWriteBehavior: behaviorWasExplicitlySet || inferredBehaviorFromMode,
    shouldWriteMode: modeWasExplicitlySet || inferredModeFromBehavior,
  };
}

export function isLiveAutoApproveEnabled(settings?: AutoApproveSettingsLike | null): boolean {
  const resolved = resolveAutoApproveSettings(settings);
  return (
    resolved.autoApproveBehavior === 'live' &&
    resolved.autoApproveMode !== DEFAULT_AUTO_APPROVE_MODE
  );
}

export function isAutoApproveEvaluationEnabled(settings?: AutoApproveSettingsLike | null): boolean {
  const resolved = resolveAutoApproveSettings(settings);
  return (
    resolved.autoApproveBehavior !== DEFAULT_AUTO_APPROVE_BEHAVIOR &&
    resolved.autoApproveMode !== DEFAULT_AUTO_APPROVE_MODE
  );
}
